/**
 * Motion path (W2-11 batch-tools; DESIGN.md §5.8, RPE Complex move CMOVE-01..06, E§4.12).
 *
 * Over [t0, t1] the line's position follows a path (pathPresets.ts: presets or x(t), y(t), optional r(t)). The
 * time curve maps linear progress w (in beats) to path progress t = curve(w), so equal curves keep the shape and
 * change the pacing. Output:
 *  - 'linear': keys every 1/perBeat beat from t0 (and at t1), joined by Linear segments: X and Y (and rotation)
 *    share every beat, so the result is isochronous by construction;
 *  - 'fit': Linear keys placed adaptively (bisection of the beat span) until the deviation is within `tolPx`.
 * The maximum deviation between the written keys and the true path is measured on 16 sub-samples per piece, with
 * Phira's interpolation (linear in SECONDS between keys), and reported in RPE px.
 * The keys go to a new additive layer by default (the lowest layer free on X, Y and, when used, rotation; it holds
 * 0 before t0 through the beat-0 Stay key of the lead-in invariant, and the path's end value after t1), or to a
 * chosen layer: there existing motion inside (t0, t1) is replaced (the curve outside stays exactly as it was) or
 * the tool refuses. X/Y-linked lines keep both axes keyed at the same beats.
 * `anchoredPreset` ("start from the line's position", the sheet's default) moves the path so its first key equals
 * what the target layer already holds just before t0 (0 on a new layer): no jump at t0, and a closed path (full
 * circle, figure-8) ends where it started.
 */

import { Ease } from '../ease/index';
import type { KeyId, LineProp, TrackValue } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat, TempoMap } from '../time/types';
import type { MessageRef } from '../util/types';
import {
  cleanLinks,
  collectSplit,
  curveCtx,
  notePartnerSplit,
  noteWorstSplit,
  ownerInfo,
  readKeys,
  valueAtBeat,
  type MotionView,
  type SplitOn,
} from '../commands/kit';
import { spanHasMotion, toolReason, TOOLS_MSG } from './common';
import { compilePath, translatePreset, type CompiledPath, type PathPoint } from './pathPresets';
import { rampCurveY } from './ramp';
import { alignXy, freeCommonLayer, sliceOf, writeSlice } from './write';
import type { MotionPathParams, PathPreset, ToolReport } from './types';

/** Most keys one path may write per axis. */
export const PATH_MAX_KEYS = 8192;
/** Largest "keys per beat" density. */
export const PATH_MAX_PER_BEAT = 1920;
/** Smallest piece the 'fit' output makes (1/PATH_FIT_MIN_DIV beat). */
export const PATH_FIT_MIN_DIV = 1024;
/** Sub-samples per piece for the deviation measurement. */
export const PATH_ERROR_SAMPLES = 16;

/** A path evaluated at chart beats (f64), with the time curve applied. */
export interface PathSampler {
  readonly path: CompiledPath;
  /** Point at a beat (f64) inside [t0, t1]; null when an expression has no finite value there. */
  at(beat: number, index: number, count: number): PathPoint | null;
}

export function pathSampler(
  tx: Pick<TxBuilder, 'tempo'>,
  path: CompiledPath,
  t0: Beat,
  t1: Beat,
  timeEase: MotionPathParams['timeEase'],
): PathSampler {
  const a = Beats.toF64(t0);
  const b = Beats.toF64(t1);
  return {
    path,
    at(beat, index, count) {
      const w = Math.min(1, Math.max(0, (beat - a) / (b - a)));
      const t = timeEase.kind === 'tween' && Ease.rpeToTweenId(timeEase.id) === 2 ? w : rampCurveY(timeEase, w);
      return path.at(t, beat, tx.tempo.secAtBeatF64(beat), index, count);
    },
  };
}

/** Uniform cut beats from t0 every 1/perBeat, plus t1. */
export function linearCuts(t0: Beat, t1: Beat, perBeat: number): Beat[] {
  const out: Beat[] = [t0];
  const step = Beats.make(0, 1, perBeat);
  for (let t = Beats.add(t0, step); Beats.lt(t, t1); t = Beats.add(t, step)) {
    out.push(t);
    if (out.length > PATH_MAX_KEYS) return out;
  }
  out.push(t1);
  return out;
}

/** Deviation (px) of the straight piece a -> b (linear in seconds) from the path between two beats. */
function pieceError(
  tx: Pick<TxBuilder, 'tempo'>,
  s: PathSampler,
  a: Beat,
  b: Beat,
  pa: PathPoint,
  pb: PathPoint,
): number {
  const fa = Beats.toF64(a);
  const fb = Beats.toF64(b);
  const sa = tx.tempo.secAtBeatF64(fa);
  const sb = tx.tempo.secAtBeatF64(fb);
  let max = 0;
  for (let j = 1; j < PATH_ERROR_SAMPLES; j++) {
    const beat = fa + ((fb - fa) * j) / PATH_ERROR_SAMPLES;
    const truth = s.at(beat, -1, 0);
    if (truth === null) continue;
    const q = (tx.tempo.secAtBeatF64(beat) - sa) / (sb - sa);
    const x = pa.x + (pb.x - pa.x) * q;
    const y = pa.y + (pb.y - pa.y) * q;
    max = Math.max(max, Math.hypot(truth.x - x, truth.y - y));
  }
  return max;
}

interface Sampled {
  readonly beats: Beat[];
  readonly points: PathPoint[];
  readonly maxError: number;
}

function sampleAt(tx: TxBuilder, s: PathSampler, beats: readonly Beat[]): PathPoint[] {
  return beats.map((b, i) => {
    const p = s.at(Beats.toF64(b), i, beats.length);
    if (p === null) tx.fail(toolReason(TOOLS_MSG.pathEval, { beat: Beats.key(b) }));
    return p;
  });
}

function sampleLinear(tx: TxBuilder, s: PathSampler, t0: Beat, t1: Beat, perBeat: number): Sampled {
  if (!Number.isSafeInteger(perBeat) || perBeat < 1 || perBeat > PATH_MAX_PER_BEAT) {
    tx.fail(toolReason(TOOLS_MSG.pathDensity, { max: PATH_MAX_PER_BEAT }));
  }
  const beats = linearCuts(t0, t1, perBeat);
  if (beats.length > PATH_MAX_KEYS) tx.fail(toolReason(TOOLS_MSG.pathTooMany, { max: PATH_MAX_KEYS }));
  const points = sampleAt(tx, s, beats);
  let maxError = 0;
  for (let i = 0; i + 1 < beats.length; i++) {
    maxError = Math.max(
      maxError,
      pieceError(tx, s, beats[i] as Beat, beats[i + 1] as Beat, points[i] as PathPoint, points[i + 1] as PathPoint),
    );
  }
  return { beats, points, maxError };
}

function sampleFit(tx: TxBuilder, s: PathSampler, t0: Beat, t1: Beat, tolPx: number): Sampled {
  if (!Number.isFinite(tolPx) || tolPx <= 0) tx.fail(toolReason(TOOLS_MSG.pathTolerance));
  const minLen = Beats.make(0, 1, PATH_FIT_MIN_DIV);
  // Start from whole beats (at least one piece), then bisect pieces that deviate more than the tolerance.
  const start = linearCuts(t0, t1, 1);
  const pointOf = new Map<string, PathPoint>();
  const at = (b: Beat): PathPoint => {
    const k = Beats.key(b);
    const hit = pointOf.get(k);
    if (hit !== undefined) return hit;
    const p = s.at(Beats.toF64(b), pointOf.size, 0);
    if (p === null) tx.fail(toolReason(TOOLS_MSG.pathEval, { beat: Beats.key(b) }));
    pointOf.set(k, p);
    return p;
  };
  const beats: Beat[] = [];
  let maxError = 0;
  const refine = (a: Beat, b: Beat): void => {
    const err = pieceError(tx, s, a, b, at(a), at(b));
    const len = Beats.sub(b, a);
    if (err > tolPx && Beats.lt(minLen, len)) {
      const mid = Beats.add(a, Beats.mulRat(len, 1, 2));
      refine(a, mid);
      refine(mid, b);
      return;
    }
    maxError = Math.max(maxError, err);
    beats.push(b);
    if (beats.length > PATH_MAX_KEYS) tx.fail(toolReason(TOOLS_MSG.pathTooMany, { max: PATH_MAX_KEYS }));
  };
  beats.push(t0);
  for (let i = 0; i + 1 < start.length; i++) refine(start[i] as Beat, start[i + 1] as Beat);
  return { beats, points: beats.map(at), maxError };
}

// ---------------------------------------------------------------------------------------------------------------
// Start from the line's position
// ---------------------------------------------------------------------------------------------------------------

/** Number of samples the tool takes (the expressions' `n`): the linear cuts, or 0 for the adaptive fit. */
function sampleCount(p: MotionPathParams): number {
  if (p.output.mode !== 'linear') return 0;
  const perBeat = p.output.perBeat;
  if (!Number.isSafeInteger(perBeat) || perBeat < 1 || perBeat > PATH_MAX_PER_BEAT) return 0;
  return linearCuts(p.t0, p.t1, perBeat).length;
}

/** The first key the tool writes (the path at t0, time curve applied), or null when it cannot be evaluated. */
export function pathStartPoint(tempo: TempoMap, p: MotionPathParams): PathPoint | null {
  if (!Beats.lt(p.t0, p.t1)) return null;
  const compiled = compilePath(p.preset, tempo);
  if (!compiled.ok) return null;
  const sampler = pathSampler({ tempo }, compiled.value, p.t0, p.t1, p.timeEase);
  return sampler.at(Beats.toF64(p.t0), 0, sampleCount(p));
}

/** What layer `layer` of a property holds just before beat t (0 for a layer without keys). */
function layerValueBefore(
  view: MotionView & { readonly tempo: TempoMap },
  p: MotionPathParams,
  prop: LineProp,
  layer: number,
): number {
  const o = ownerInfo(view, { kind: 'line', line: p.line, prop, layer });
  if (o === null || !o.exists) return 0;
  const v = valueAtBeat(readKeys(view, o.info.track.id), p.t0, curveCtx(view, o.info), 'left');
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * The preset moved so the path starts where the target layer already is just before t0 (0 on a new layer; see
 * the file header). Unchanged when the path cannot be evaluated (the tool then reports why).
 */
export function anchoredPreset(view: MotionView & { readonly tempo: TempoMap }, p: MotionPathParams): PathPreset {
  const start = pathStartPoint(view.tempo, p);
  if (start === null) return p.preset;
  const layer = p.layer;
  const existing = layer !== 'new' && Number.isSafeInteger(layer) && layer >= 0 && view.doc.lines.has(p.line);
  const base = (prop: LineProp): number => (existing ? layerValueBefore(view, p, prop, layer) : 0);
  return translatePreset(p.preset, base('moveX') - start.x, base('moveY') - start.y, base('rotate') - start.r);
}

export function motionPath(tx: TxBuilder, p: MotionPathParams): ToolReport {
  if (!tx.doc.lines.has(p.line)) tx.fail(toolReason(TOOLS_MSG.lineMissing));
  if (Beats.cmp(p.t0, Beats.ZERO) < 0 || !Beats.lt(p.t0, p.t1)) tx.fail(toolReason(TOOLS_MSG.pathRange));
  const compiled = compilePath(p.preset, tx.tempo);
  if (!compiled.ok) tx.fail(toolReason(TOOLS_MSG.pathExpr, { axis: compiled.error.axis }));
  const sampler = pathSampler(tx, compiled.value, p.t0, p.t1, p.timeEase);
  const sampled =
    p.output.mode === 'linear'
      ? sampleLinear(tx, sampler, p.t0, p.t1, p.output.perBeat)
      : sampleFit(tx, sampler, p.t0, p.t1, p.output.tolPx);

  const props: LineProp[] = compiled.value.rotates ? ['moveX', 'moveY', 'rotate'] : ['moveX', 'moveY'];
  let layer: number;
  const notes: MessageRef[] = [];
  if (p.layer === 'new') {
    const free = freeCommonLayer(tx, p.line, props);
    if (free === null) tx.fail(toolReason(TOOLS_MSG.pathNoFreeLayer));
    layer = free;
    notes.push(toolReason(TOOLS_MSG.pathLayer, { layer }));
  } else {
    if (!Number.isSafeInteger(p.layer) || p.layer < 0) tx.fail(toolReason(TOOLS_MSG.pathLayerInvalid));
    layer = p.layer;
    if (p.conflict === 'refuse') {
      for (const prop of props) {
        const track = tx.idx.track(p.line, prop, layer);
        if (track !== undefined && spanHasMotion(readKeys(tx, track), p.t0, p.t1)) {
          tx.fail(toolReason(TOOLS_MSG.pathOverlap));
        }
      }
    }
  }

  const unlinked = new Set<number>();
  let worstSplit: SplitOn | null = null;
  const segments: KeyId[] = [];
  let written = 0;
  const valuesOf = (prop: LineProp): TrackValue[] =>
    sampled.points.map((pt) => {
      const v = prop === 'moveX' ? pt.x : prop === 'moveY' ? pt.y : pt.r;
      return v === 0 ? 0 : v;
    });
  for (const prop of props) {
    const w = writeSlice(tx, p.line, prop, layer, sliceOf(sampled.beats, valuesOf(prop)), unlinked);
    worstSplit = collectSplit(worstSplit, w.info, w.report);
    written += w.ids.length;
    segments.push(...w.ids.slice(0, -1));
  }
  const partner = alignXy(tx, p.line, layer, [[p.t0, p.t1]], ['moveX', 'moveY'], unlinked);
  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, worstSplit);
  notePartnerSplit(tx, partner);
  if (p.output.mode === 'fit') {
    notes.push(toolReason(TOOLS_MSG.pathFitted, { count: sampled.beats.length, tolerance: p.output.tolPx }));
  }
  for (const n of notes) tx.note(n);
  tx.setSelection({ segments, keys: [], notes: [], activeLine: p.line });
  tx.setFocus({ beat: p.t0, line: p.line });
  return { changed: written, maxError: sampled.maxError, notes };
}
